import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tag, Mail, BarChart3, Plus, Trash2, Send, Edit2, Copy, Megaphone, TrendingUp, Sparkles, Loader2, Zap, MessageCircle, Instagram, Facebook, Mail as MailIcon } from "lucide-react";
import { toast, Toaster } from "sonner";
import { MailchimpTab } from "../../components/MailchimpTab";

function isoDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

const TABS = [
    { key: "quickstart", label: "Quick start", icon: Zap },
    { key: "launcher", label: "AI launcher", icon: Sparkles },
    { key: "coupons", label: "Coupons", icon: Tag },
    { key: "campaigns", label: "Email campaigns", icon: Mail },
    { key: "mailchimp", label: "Mailchimp", icon: MailIcon },
    { key: "social", label: "Social templates", icon: MessageCircle },
    { key: "analytics", label: "Campaign analytics", icon: BarChart3 },
];

export default function AdminMarketing() {
    const [tab, setTab] = useState("quickstart");
    return (
        <div className="space-y-5" data-testid="admin-marketing-page">
            <Toaster position="top-center" />
            <div>
                <div className="label-eyebrow text-sky-700">Growth</div>
                <h1 className="font-serif text-4xl text-stone-900">Marketing tools</h1>
                <p className="text-sm text-stone-500 mt-1">One-click campaigns, AI-written social copy, coupons that auto-discount the cart, email blasts, and UTM attribution.</p>
            </div>
            <div className="flex flex-wrap gap-1 border-b border-stone-200">
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                        className={`px-4 py-3 -mb-px flex items-center gap-2 text-sm border-b-2 ${tab === t.key ? "border-sky-700 text-sky-700 font-semibold bg-white" : "border-transparent text-stone-500 hover:text-stone-900 hover:bg-stone-50"}`}>
                        <t.icon className="w-4 h-4" />{t.label}
                    </button>
                ))}
            </div>
            {tab === "quickstart" && <QuickStartTab onJump={setTab} />}
            {tab === "launcher" && <LauncherTab />}
            {tab === "coupons" && <CouponsTab />}
            {tab === "campaigns" && <CampaignsTab />}
            {tab === "mailchimp" && <MailchimpTab />}
            {tab === "social" && <SocialTab />}
            {tab === "analytics" && <AnalyticsTab />}
        </div>
    );
}

// ----------------- QUICKSTART -----------------
const QUICK_COUPONS = [
    { label: "Welcome 10% off (first-time)", icon: "🎁", payload: { code: "WELCOME10", type: "percent", value: 10, min_subtotal: 499, per_user_limit: 1, description: "First-order welcome discount", active: true, applies_to: "retail" } },
    { label: "Festive Sale 15%", icon: "✨", payload: { code: "FESTIVE15", type: "percent", value: 15, min_subtotal: 999, max_discount: 500, per_user_limit: 1, description: "Festive season special", active: true, applies_to: "retail" } },
    { label: "Free shipping (flat ₹60 off)", icon: "🚚", payload: { code: "FREESHIP", type: "flat", value: 60, min_subtotal: 499, per_user_limit: 2, description: "Free shipping offer", active: true, applies_to: "retail" } },
    { label: "Win-back 20% (dormant)", icon: "💌", payload: { code: "COMEBACK20", type: "percent", value: 20, min_subtotal: 699, max_discount: 400, per_user_limit: 1, description: "We miss you — 20% off", active: true, applies_to: "retail" } },
    { label: "B2B distributor bulk 8%", icon: "📦", payload: { code: "BULK8", type: "percent", value: 8, min_subtotal: 5000, per_user_limit: 5, description: "Distributor bulk discount", active: true, applies_to: "all" } },
];

function QuickStartTab({ onJump }) {
    const [busy, setBusy] = useState("");
    const create = async (q) => {
        setBusy(q.label);
        try {
            await api.post("/admin/coupons", q.payload);
            toast.success(`Coupon ${q.payload.code} created — visible in Coupons tab`);
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create"); }
        finally { setBusy(""); }
    };
    return (
        <div className="space-y-6" data-testid="quickstart-tab">
            <div>
                <h2 className="font-serif text-xl text-stone-900 mb-1">One-click campaign templates</h2>
                <p className="text-xs text-stone-500 mb-4">Pick a ready-made template and we'll set up the coupon for you. Adjust details later in the Coupons tab.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {QUICK_COUPONS.map(q => (
                        <button key={q.label} onClick={() => create(q)} disabled={busy === q.label} className="text-left bg-white border border-stone-200 hover:border-sky-300 hover:shadow-sm p-4 transition-all disabled:opacity-50" data-testid={`quick-coupon-${q.payload.code}`}>
                            <div className="text-2xl mb-2">{q.icon}</div>
                            <div className="font-medium text-stone-900">{q.label}</div>
                            <div className="text-xs text-stone-500 mt-1 font-mono">{q.payload.code} · {q.payload.type === "percent" ? `${q.payload.value}% off` : `₹${q.payload.value} off`}</div>
                            <div className="text-[10px] text-sky-700 mt-2 inline-flex items-center gap-1">{busy === q.label ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}{busy === q.label ? "Creating…" : "One-click create"}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <button onClick={() => onJump("social")} className="text-left bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 hover:border-purple-400 p-5" data-testid="jump-social">
                    <Sparkles className="w-6 h-6 text-purple-700 mb-2" />
                    <div className="font-serif text-xl text-stone-900">Generate social copy</div>
                    <p className="text-xs text-stone-600 mt-1">Tell us your campaign theme and we'll write ready-to-post copy for WhatsApp, Instagram, Facebook, X &amp; email.</p>
                </button>
                <button onClick={() => onJump("campaigns")} className="text-left bg-sky-50 border border-sky-200 hover:border-sky-400 p-5" data-testid="jump-campaigns">
                    <Mail className="w-6 h-6 text-sky-700 mb-2" />
                    <div className="font-serif text-xl text-stone-900">Send a campaign email</div>
                    <p className="text-xs text-stone-600 mt-1">Compose once, send to a smart segment (all customers, opted-in, or dormant 30d).</p>
                </button>
            </div>

            <div className="bg-white border border-stone-200 p-5">
                <h3 className="font-serif text-lg text-stone-900 mb-3">How attribution works</h3>
                <ol className="text-xs text-stone-600 space-y-2 list-decimal pl-5">
                    <li>Share a tagged URL like <code className="bg-stone-100 px-1 font-mono text-[11px]">https://hplmedicines.com/shop?utm_source=instagram&amp;utm_campaign=festive&amp;coupon=FESTIVE15</code>.</li>
                    <li>Visitors land on the site — we capture the UTMs &amp; coupon for them automatically.</li>
                    <li>If they buy, the order is attributed to that campaign and the coupon is auto-applied at checkout.</li>
                    <li>See revenue per source in the <button onClick={() => onJump("analytics")} className="text-sky-700 underline">Campaign analytics</button> tab.</li>
                </ol>
            </div>
        </div>
    );
}

// ----------------- AI CAMPAIGN LAUNCHER -----------------
function LauncherTab() {
    const [input, setInput] = useState({ goal: "", audience: "all_customers", cta_url: "https://hplmedicines.com/shop" });
    const [output, setOutput] = useState(null);
    const [busy, setBusy] = useState(false);
    const [savedCoupon, setSavedCoupon] = useState(false);
    const [campaignSaved, setCampaignSaved] = useState(false);

    const launch = async () => {
        if (input.goal.trim().length < 5) return toast.error("Tell us your goal (at least 5 chars)");
        setBusy(true); setOutput(null); setSavedCoupon(false); setCampaignSaved(false);
        try {
            const { data } = await api.post("/admin/ai/campaign-launcher", input);
            setOutput(data);
            toast.success("Campaign drafted — review, save the coupon, blast the email");
        } catch (e) { toast.error(e.response?.data?.detail || "AI couldn't draft this"); }
        finally { setBusy(false); }
    };

    const saveCoupon = async () => {
        try {
            await api.post("/admin/coupons", { ...output.coupon, active: true, applies_to: "all" });
            setSavedCoupon(true);
            toast.success(`Coupon ${output.coupon.code} created`);
        } catch (e) {
            // Already exists is fine
            if (e.response?.data?.detail?.includes("already exists")) { setSavedCoupon(true); toast.success("Coupon already exists — using it"); }
            else toast.error(e.response?.data?.detail || "Couldn't save coupon");
        }
    };
    const createCampaign = async (sendNow = false) => {
        try {
            await api.post("/admin/campaigns", {
                name: `AI · ${output.coupon.code}`,
                subject: output.email_subject,
                body_text: output.email_body,
                audience: output.audience,
                send_now: sendNow,
            });
            setCampaignSaved(true);
            toast.success(sendNow ? "Campaign sent" : "Campaign saved as draft");
        } catch (e) { toast.error(e.response?.data?.detail); }
    };
    const copyTo = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

    return (
        <div className="space-y-5" data-testid="launcher-tab">
            <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 border border-purple-200 p-6">
                <div className="flex items-start gap-3">
                    <Sparkles className="w-6 h-6 text-purple-700 mt-1" />
                    <div className="flex-1">
                        <h2 className="font-serif text-2xl text-stone-900">AI Campaign Launcher</h2>
                        <p className="text-sm text-stone-600 mt-1">Type your goal once — we'll generate the coupon code, email subject + body, and social copy for 4 platforms, plus a UTM-tagged share link. One prompt → complete campaign.</p>
                    </div>
                </div>
                <div className="space-y-3 mt-5">
                    <L label="Your goal — describe the campaign in one sentence">
                        <Textarea
                            value={input.goal}
                            onChange={e => setInput({ ...input, goal: e.target.value })}
                            placeholder="e.g. Diwali sale, push Arnica & Calendula products, 20% off for orders above ₹999"
                            rows={3}
                            className="rounded-none border-stone-300 bg-white"
                            data-testid="launcher-goal"
                        />
                    </L>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <L label="Audience">
                            <Select value={input.audience} onValueChange={v => setInput({ ...input, audience: v })}>
                                <SelectTrigger className="rounded-none border-stone-300 bg-white" data-testid="launcher-audience"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all_customers">All customers</SelectItem>
                                    <SelectItem value="opted_in">Opted-in only</SelectItem>
                                    <SelectItem value="dormant_30d">Dormant (no order 30d)</SelectItem>
                                    <SelectItem value="all_distributors">All distributors</SelectItem>
                                </SelectContent>
                            </Select>
                        </L>
                        <L label="Landing URL"><Input value={input.cta_url} onChange={e => setInput({ ...input, cta_url: e.target.value })} className="rounded-none border-stone-300 bg-white font-mono text-xs" data-testid="launcher-url" /></L>
                    </div>
                    <Button onClick={launch} disabled={busy} className="rounded-none bg-purple-700 hover:bg-purple-800 text-stone-50" data-testid="launcher-go">
                        {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Drafting full campaign…</> : <><Sparkles className="w-4 h-4 mr-1" />Launch with AI</>}
                    </Button>
                </div>
            </div>

            {output && (
                <div className="space-y-4" data-testid="launcher-output">
                    {/* Coupon card */}
                    <div className="bg-white border border-stone-200 p-5">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                            <h3 className="font-serif text-lg text-stone-900 inline-flex items-center gap-2"><Tag className="w-4 h-4 text-sky-700" />Coupon</h3>
                            <Button onClick={saveCoupon} disabled={savedCoupon} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 h-8" data-testid="launcher-save-coupon">
                                {savedCoupon ? "✓ Saved" : <><Plus className="w-3.5 h-3.5 mr-1" />Save coupon</>}
                            </Button>
                        </div>
                        <div className="grid sm:grid-cols-4 gap-3 text-sm">
                            <div><div className="label-eyebrow text-stone-500">Code</div><div className="font-mono font-semibold text-stone-900 mt-1">{output.coupon.code}</div></div>
                            <div><div className="label-eyebrow text-stone-500">Discount</div><div className="font-semibold mt-1">{output.coupon.type === "percent" ? `${output.coupon.value}%` : formatINR(output.coupon.value)}{output.coupon.max_discount ? ` (max ${formatINR(output.coupon.max_discount)})` : ""}</div></div>
                            <div><div className="label-eyebrow text-stone-500">Min order</div><div className="mt-1">{formatINR(output.coupon.min_subtotal || 0)}</div></div>
                            <div className="sm:col-span-1"><div className="label-eyebrow text-stone-500">Description</div><div className="text-xs mt-1">{output.coupon.description}</div></div>
                        </div>
                    </div>

                    {/* Share link */}
                    <div className="bg-emerald-50 border border-emerald-200 p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="label-eyebrow text-emerald-700">UTM-tagged share link</div>
                                <div className="text-xs font-mono text-emerald-900 break-all mt-1" data-testid="launcher-share-link">{output.share_link}</div>
                                <div className="text-[10px] text-emerald-700 mt-1">Visits via this link will be attributed in Campaign analytics + the coupon is auto-applied.</div>
                            </div>
                            <Button onClick={() => copyTo(output.share_link)} variant="outline" className="rounded-none border-emerald-400 text-emerald-800" data-testid="launcher-copy-link">
                                <Copy className="w-3.5 h-3.5 mr-1" />Copy link
                            </Button>
                        </div>
                    </div>

                    {/* Email block */}
                    <div className="bg-white border border-stone-200 p-5">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                            <h3 className="font-serif text-lg text-stone-900 inline-flex items-center gap-2"><MailIcon className="w-4 h-4 text-amber-700" />Email · audience: {output.audience}</h3>
                            <div className="flex gap-2">
                                <Button onClick={() => createCampaign(false)} variant="outline" className="rounded-none" data-testid="launcher-save-draft">Save as draft</Button>
                                <Button onClick={() => createCampaign(true)} disabled={campaignSaved} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="launcher-send">
                                    <Send className="w-3.5 h-3.5 mr-1" />{campaignSaved ? "Sent ✓" : "Send now"}
                                </Button>
                            </div>
                        </div>
                        <div className="text-xs">
                            <div className="font-semibold text-stone-900 mb-1">{output.email_subject}</div>
                            <pre className="bg-stone-50 border border-stone-200 p-3 whitespace-pre-wrap font-sans leading-relaxed">{output.email_body}</pre>
                        </div>
                    </div>

                    {/* Social copy */}
                    <div className="bg-white border border-stone-200 p-5">
                        <h3 className="font-serif text-lg text-stone-900 mb-3 inline-flex items-center gap-2"><MessageCircle className="w-4 h-4 text-pink-700" />Social media copy</h3>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {["whatsapp", "instagram", "facebook", "x_twitter"].map(p => (
                                <div key={p} className="border border-stone-200" data-testid={`launcher-social-${p}`}>
                                    <div className="px-3 py-1.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                                        <span className="text-xs font-semibold uppercase tracking-widest text-stone-600">{p.replace("_", " · ")}</span>
                                        <button onClick={() => copyTo(output.social[p] || "")} className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1"><Copy className="w-3 h-3" />Copy</button>
                                    </div>
                                    <pre className="text-xs p-3 whitespace-pre-wrap font-sans leading-relaxed">{output.social[p] || "—"}</pre>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ----------------- SOCIAL TEMPLATES (AI) -----------------
const PLATFORMS = [
    { k: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "emerald" },
    { k: "instagram", label: "Instagram", icon: Instagram, color: "pink" },
    { k: "facebook", label: "Facebook", icon: Facebook, color: "blue" },
    { k: "x_twitter", label: "X (Twitter)", icon: MessageCircle, color: "stone" },
    { k: "email_subject", label: "Email subject", icon: MailIcon, color: "amber" },
    { k: "email_body", label: "Email body", icon: MailIcon, color: "amber" },
];

function SocialTab() {
    const [input, setInput] = useState({ occasion: "", cta_url: "", coupon_code: "", tone: "professional" });
    const [output, setOutput] = useState(null);
    const [busy, setBusy] = useState(false);

    const gen = async () => {
        if (input.occasion.trim().length < 5) return toast.error("Tell us your campaign theme (≥5 chars)");
        setBusy(true);
        try {
            const { data } = await api.post("/admin/ai/social-templates", input);
            setOutput(data);
            toast.success("Copy ready — click any block to copy it");
        } catch (e) { toast.error(e.response?.data?.detail || "AI couldn't draft copy"); }
        finally { setBusy(false); }
    };

    const copyTo = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };

    return (
        <div className="space-y-4" data-testid="social-tab">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 p-5">
                <h2 className="font-serif text-xl text-stone-900 inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-700" />AI Social Copy Writer</h2>
                <p className="text-xs text-stone-600 mt-1 mb-4">Describe your campaign — we'll write Insta, Facebook, WhatsApp, X and email copy in one shot.</p>
                <div className="space-y-3">
                    <L label="What's the campaign about?"><Textarea value={input.occasion} onChange={e => setInput({ ...input, occasion: e.target.value })} placeholder="e.g. Monsoon flu season — 20% off cold &amp; flu remedies for the next 5 days" rows={3} className="rounded-none border-stone-300" data-testid="social-input-occasion" /></L>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <L label="CTA link (optional)"><Input value={input.cta_url} onChange={e => setInput({ ...input, cta_url: e.target.value })} placeholder="https://hplmedicines.com/shop" className="rounded-none border-stone-300 font-mono text-xs" data-testid="social-input-url" /></L>
                        <L label="Coupon code (optional)"><Input value={input.coupon_code} onChange={e => setInput({ ...input, coupon_code: e.target.value.toUpperCase() })} placeholder="MONSOON20" className="rounded-none border-stone-300 font-mono uppercase" data-testid="social-input-coupon" /></L>
                        <L label="Tone">
                            <Select value={input.tone} onValueChange={v => setInput({ ...input, tone: v })}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="social-input-tone"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="professional">Professional</SelectItem>
                                    <SelectItem value="festive">Festive</SelectItem>
                                    <SelectItem value="playful">Playful</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </L>
                    </div>
                    <Button onClick={gen} disabled={busy} className="rounded-none bg-purple-700 hover:bg-purple-800 text-stone-50" data-testid="social-generate">
                        {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Drafting…</> : <><Sparkles className="w-4 h-4 mr-1" />Generate copy for all platforms</>}
                    </Button>
                </div>
            </div>

            {output && (
                <div className="grid sm:grid-cols-2 gap-4">
                    {PLATFORMS.map(p => (
                        <div key={p.k} className="bg-white border border-stone-200" data-testid={`social-out-${p.k}`}>
                            <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                                <div className="text-sm font-medium text-stone-900 inline-flex items-center gap-2"><p.icon className="w-4 h-4" />{p.label}</div>
                                <button onClick={() => copyTo(output[p.k] || "")} className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1" data-testid={`social-copy-${p.k}`}>
                                    <Copy className="w-3 h-3" />Copy
                                </button>
                            </div>
                            <pre className="text-xs text-stone-800 p-4 whitespace-pre-wrap font-sans leading-relaxed">{output[p.k] || "—"}</pre>
                            <div className="px-4 py-1.5 border-t border-stone-100 text-[10px] text-stone-500">{(output[p.k] || "").length} chars</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ----------------- COUPONS -----------------
const EMPTY_COUPON = { code: "", type: "percent", value: 10, min_subtotal: 0, max_discount: "", valid_from: "", valid_until: "", usage_limit: "", per_user_limit: 1, active: true, description: "", applies_to: "all" };
function CouponsTab() {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = async () => { const { data } = await api.get("/admin/coupons"); setRows(data); };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing.code.trim()) return toast.error("Code is required");
        const payload = {
            ...editing,
            code: editing.code.trim().toUpperCase(),
            value: Number(editing.value),
            min_subtotal: Number(editing.min_subtotal || 0),
            max_discount: editing.max_discount === "" ? null : Number(editing.max_discount),
            usage_limit: editing.usage_limit === "" ? null : Number(editing.usage_limit),
            per_user_limit: editing.per_user_limit === "" ? null : Number(editing.per_user_limit),
            valid_from: editing.valid_from || null,
            valid_until: editing.valid_until || null,
        };
        try {
            if (editing.id) await api.put(`/admin/coupons/${editing.id}`, payload);
            else await api.post("/admin/coupons", payload);
            toast.success("Coupon saved"); setEditing(null); await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };
    const remove = async (r) => {
        if (!window.confirm(`Delete coupon "${r.code}"?`)) return;
        try { await api.delete(`/admin/coupons/${r.id}`); toast.success("Deleted"); await load(); } catch (e) { toast.error(e.response?.data?.detail); }
    };
    const copyShare = (r) => {
        const url = `${window.location.origin}/?coupon=${r.code}&utm_source=email&utm_campaign=${r.code.toLowerCase()}`;
        navigator.clipboard.writeText(url); toast.success("Shareable link copied");
    };

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Button onClick={() => setEditing({ ...EMPTY_COUPON })} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="coupon-new"><Plus className="w-4 h-4 mr-1" />New coupon</Button>
            </div>
            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-stone-50 border-b border-stone-200">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Code</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Discount</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Validity</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Used</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Revenue</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>
                    </tr></thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={7} className="p-10 text-center text-sm text-stone-500"><Tag className="w-7 h-7 mx-auto mb-2 text-stone-300" />No coupons yet — create one to start a campaign.</td></tr>
                        ) : rows.map(r => (
                            <tr key={r.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`coupon-row-${r.id}`}>
                                <td className="p-3"><div className="font-mono font-semibold text-stone-900">{r.code}</div>{r.description && <div className="text-[10px] text-stone-500">{r.description}</div>}</td>
                                <td className="p-3 text-stone-900 font-semibold">{r.type === "percent" ? `${r.value}%` : formatINR(r.value)}{r.max_discount ? ` (max ${formatINR(r.max_discount)})` : ""}{r.min_subtotal > 0 ? <div className="text-[10px] text-stone-500 font-normal">min order: {formatINR(r.min_subtotal)}</div> : null}</td>
                                <td className="p-3 text-xs text-stone-600">{r.valid_from || "anytime"} → {r.valid_until || "no end"}</td>
                                <td className="p-3 text-right font-mono">{r.used_count || 0}{r.usage_limit ? ` / ${r.usage_limit}` : ""}</td>
                                <td className="p-3 text-right font-mono text-emerald-700">{formatINR(r.revenue_attributed || 0)}</td>
                                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${r.active ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-600"}`}>{r.active ? "Active" : "Off"}</span></td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex gap-1">
                                        <Button size="sm" variant="outline" className="rounded-none h-7 px-2" onClick={() => copyShare(r)} data-testid={`coupon-share-${r.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                                        <Button size="sm" variant="outline" className="rounded-none h-7 px-2" onClick={() => setEditing({ ...r, max_discount: r.max_discount ?? "", usage_limit: r.usage_limit ?? "" })} data-testid={`coupon-edit-${r.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                        <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-red-300 text-red-700" onClick={() => remove(r)} data-testid={`coupon-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
                    <DialogContent className="rounded-none max-w-2xl">
                        <DialogHeader><DialogTitle className="font-serif text-2xl">{editing.id ? "Edit coupon" : "New coupon"}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <L label="Code (uppercase)"><Input value={editing.code} onChange={e => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className="rounded-none border-stone-300 font-mono" data-testid="coupon-field-code" /></L>
                                <L label="Type">
                                    <Select value={editing.type} onValueChange={v => setEditing({ ...editing, type: v })}>
                                        <SelectTrigger className="rounded-none border-stone-300" data-testid="coupon-field-type"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="percent">Percent (%)</SelectItem><SelectItem value="flat">Flat (₹)</SelectItem></SelectContent>
                                    </Select>
                                </L>
                                <L label="Value"><Input type="number" value={editing.value} onChange={e => setEditing({ ...editing, value: e.target.value })} className="rounded-none border-stone-300" data-testid="coupon-field-value" /></L>
                                <L label="Min subtotal (₹)"><Input type="number" value={editing.min_subtotal} onChange={e => setEditing({ ...editing, min_subtotal: e.target.value })} className="rounded-none border-stone-300" data-testid="coupon-field-min" /></L>
                                <L label="Max discount cap (₹)"><Input type="number" value={editing.max_discount} onChange={e => setEditing({ ...editing, max_discount: e.target.value })} placeholder="no cap" className="rounded-none border-stone-300" /></L>
                                <L label="Total usage limit"><Input type="number" value={editing.usage_limit} onChange={e => setEditing({ ...editing, usage_limit: e.target.value })} placeholder="unlimited" className="rounded-none border-stone-300" /></L>
                                <L label="Per-user limit"><Input type="number" value={editing.per_user_limit} onChange={e => setEditing({ ...editing, per_user_limit: e.target.value })} className="rounded-none border-stone-300" /></L>
                                <L label="Valid from (YYYY-MM-DD)"><Input type="date" value={editing.valid_from} onChange={e => setEditing({ ...editing, valid_from: e.target.value })} className="rounded-none border-stone-300" /></L>
                                <L label="Valid until"><Input type="date" value={editing.valid_until} onChange={e => setEditing({ ...editing, valid_until: e.target.value })} className="rounded-none border-stone-300" /></L>
                                <L label="Applies to">
                                    <Select value={editing.applies_to} onValueChange={v => setEditing({ ...editing, applies_to: v })}>
                                        <SelectTrigger className="rounded-none border-stone-300"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="all">All carts</SelectItem><SelectItem value="retail">Retail only</SelectItem></SelectContent>
                                    </Select>
                                </L>
                            </div>
                            <L label="Description (shown to customer at checkout)"><Input value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} className="rounded-none border-stone-300" /></L>
                            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="coupon-field-active" />Active</label>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
                            <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="coupon-save">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

// ----------------- CAMPAIGNS -----------------
const EMPTY_CAMP = { name: "", subject: "", body_text: "", audience: "all_customers", send_now: false, test_email: "" };
function CampaignsTab() {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const [counts, setCounts] = useState({});
    const load = async () => {
        const { data } = await api.get("/admin/campaigns"); setRows(data);
        const c = await api.get("/admin/campaigns/audience-counts"); setCounts(c.data);
    };
    useEffect(() => { load(); }, []);

    const save = async (sendNow = false) => {
        if (!editing.name.trim() || !editing.subject.trim() || !editing.body_text.trim()) return toast.error("Name, subject and body are required");
        try {
            await api.post("/admin/campaigns", { ...editing, send_now: sendNow });
            toast.success(sendNow ? "Campaign sent" : "Draft saved");
            setEditing(null); await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const sendNow = async (c) => {
        if (!window.confirm(`Send "${c.name}" to the ${c.audience} audience now?`)) return;
        try { await api.post(`/admin/campaigns/${c.id}/send`); toast.success("Sent"); await load(); } catch (e) { toast.error(e.response?.data?.detail); }
    };
    const sendTest = async (c) => {
        const email = window.prompt("Send a test to which email?");
        if (!email) return;
        try { await api.post(`/admin/campaigns/${c.id}/send?test_email=${encodeURIComponent(email)}`); toast.success("Test sent"); } catch (e) { toast.error(e.response?.data?.detail); }
    };
    const remove = async (c) => {
        if (!window.confirm(`Delete "${c.name}"?`)) return;
        try { await api.delete(`/admin/campaigns/${c.id}`); toast.success("Deleted"); await load(); } catch (e) { toast.error(e.response?.data?.detail); }
    };

    const AUD_LABELS = { all_customers: "All customers", all_distributors: "All distributors", opted_in: "Marketing opt-in only", dormant_30d: "Dormant (no order 30d)" };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-xs text-stone-500 flex gap-3 flex-wrap">
                    {Object.entries(counts).map(([k, n]) => <span key={k} data-testid={`audience-${k}`}>{AUD_LABELS[k] || k}: <strong className="text-stone-800">{n}</strong></span>)}
                </div>
                <Button onClick={() => setEditing({ ...EMPTY_CAMP })} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="campaign-new"><Plus className="w-4 h-4 mr-1" />New campaign</Button>
            </div>
            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-stone-50 border-b border-stone-200">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Name</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Subject</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Audience</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Sent</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Sent at</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>
                    </tr></thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={7} className="p-10 text-center text-sm text-stone-500"><Mail className="w-7 h-7 mx-auto mb-2 text-stone-300" />No campaigns yet.</td></tr>
                        ) : rows.map(c => (
                            <tr key={c.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`campaign-row-${c.id}`}>
                                <td className="p-3 font-medium text-stone-900">{c.name}</td>
                                <td className="p-3 text-stone-700">{c.subject}</td>
                                <td className="p-3 text-xs text-stone-600">{AUD_LABELS[c.audience] || c.audience}</td>
                                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${c.status === "sent" ? "bg-emerald-100 text-emerald-800" : c.status === "failed" ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-600"}`}>{c.status}</span></td>
                                <td className="p-3 text-right font-mono">{c.sent_count || 0}{c.failed_count ? <span className="text-red-600"> · {c.failed_count} failed</span> : ""}</td>
                                <td className="p-3 text-[11px] text-stone-500">{c.sent_at ? c.sent_at.slice(0, 16).replace("T", " ") : "—"}</td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex gap-1">
                                        {c.status === "draft" && <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-sky-300 text-sky-700" onClick={() => sendNow(c)} data-testid={`campaign-send-${c.id}`}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>}
                                        <Button size="sm" variant="outline" className="rounded-none h-7 px-2" onClick={() => sendTest(c)} data-testid={`campaign-test-${c.id}`}>Test</Button>
                                        <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-red-300 text-red-700" onClick={() => remove(c)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
                    <DialogContent className="rounded-none max-w-2xl">
                        <DialogHeader><DialogTitle className="font-serif text-2xl">New email campaign</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <L label="Internal name"><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="rounded-none border-stone-300" data-testid="campaign-field-name" /></L>
                            <L label="Subject line"><Input value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })} className="rounded-none border-stone-300" data-testid="campaign-field-subject" /></L>
                            <L label="Audience">
                                <Select value={editing.audience} onValueChange={v => setEditing({ ...editing, audience: v })}>
                                    <SelectTrigger className="rounded-none border-stone-300" data-testid="campaign-field-audience"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all_customers">All customers ({counts.all_customers ?? 0})</SelectItem>
                                        <SelectItem value="all_distributors">All distributors ({counts.all_distributors ?? 0})</SelectItem>
                                        <SelectItem value="opted_in">Marketing opt-in ({counts.opted_in ?? 0})</SelectItem>
                                        <SelectItem value="dormant_30d">Dormant 30d ({counts.dormant_30d ?? 0})</SelectItem>
                                    </SelectContent>
                                </Select>
                            </L>
                            <L label="Body (you can use {name} placeholder)"><Textarea rows={10} value={editing.body_text} onChange={e => setEditing({ ...editing, body_text: e.target.value })} className="rounded-none border-stone-300 font-mono text-xs" data-testid="campaign-field-body" /></L>
                            <div className="text-xs text-stone-500">Tip: include a CTA link with UTM tags to attribute orders. e.g. <code className="bg-stone-100 px-1">{`{site}/shop?utm_source=email&utm_campaign=spring`}</code></div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
                            <Button variant="outline" className="rounded-none" onClick={() => save(false)} data-testid="campaign-save-draft">Save draft</Button>
                            <Button onClick={() => save(true)} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="campaign-save-send"><Send className="w-4 h-4 mr-1" />Save & send</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

// ----------------- ANALYTICS -----------------
function AnalyticsTab() {
    const [fromDate, setFromDate] = useState(isoDaysAgo(29));
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
    const [data, setData] = useState(null);
    const load = async (f = fromDate, t = toDate) => {
        const { data } = await api.get("/admin/marketing/analytics", { params: { from_date: f, to_date: t } });
        setData(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    return (
        <div className="space-y-5">
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-end gap-3 flex-wrap bg-white border border-stone-200 p-4">
                <div><label className="text-[10px] uppercase tracking-widest text-stone-500">From</label><Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="analytics-from" /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-stone-500">To</label><Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="analytics-to" /></div>
                <Button type="submit" className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Apply</Button>
            </form>
            {data && (
                <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        <Kpi label="Orders" v={data.totals.orders} />
                        <Kpi label="Revenue" v={formatINR(data.totals.revenue)} />
                        <Kpi label="UTM attributed" v={`${data.totals.attribution_pct}%`} sub={`${formatINR(data.totals.attributed_revenue)} from ${data.totals.attributed_orders} orders`} />
                        <Kpi label="Discount given" v={formatINR(data.totals.total_discount)} />
                        <Kpi label="Coupon redemptions" v={data.totals.coupons_used} />
                    </div>

                    <Section title="Top UTM sources" empty="No UTM-tagged orders in this period." icon={Megaphone}>
                        {data.top_sources.length > 0 && (
                            <div className="bg-white border border-stone-200">
                                {data.top_sources.map(s => {
                                    const max = data.top_sources[0].revenue || 1;
                                    return (
                                        <div key={s.source} className="flex items-center gap-3 p-3 border-b border-stone-100 last:border-0">
                                            <span className="w-28 text-xs uppercase tracking-widest text-stone-500">{s.source}</span>
                                            <div className="flex-1 h-4 bg-stone-100"><div className="h-full bg-sky-400" style={{ width: `${(s.revenue / max) * 100}%` }} /></div>
                                            <span className="w-32 text-right font-mono text-sm text-emerald-700">{formatINR(s.revenue)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Section>

                    <Section title="Campaigns (utm_source / medium / campaign)" empty="No UTM-tagged orders in this period." icon={TrendingUp}>
                        {data.campaigns.length > 0 && (
                            <div className="bg-white border border-stone-200 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="bg-stone-50 border-b border-stone-200">
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Source</th>
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Medium</th>
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Campaign</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Orders</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Customers</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">AOV</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Discount</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Revenue</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.campaigns.map((r, i) => (
                                            <tr key={`${r.utm_source}-${r.utm_medium}-${r.utm_campaign}-${i}`} className="border-b border-stone-100 hover:bg-sky-50/40">
                                                <td className="p-3 font-mono text-xs">{r.utm_source}</td>
                                                <td className="p-3 font-mono text-xs">{r.utm_medium}</td>
                                                <td className="p-3 font-mono text-xs">{r.utm_campaign}</td>
                                                <td className="p-3 text-right">{r.orders}</td>
                                                <td className="p-3 text-right">{r.unique_customers}</td>
                                                <td className="p-3 text-right font-mono">{formatINR(r.avg_order_value)}</td>
                                                <td className="p-3 text-right text-amber-700 font-mono">{formatINR(r.discount)}</td>
                                                <td className="p-3 text-right font-mono text-emerald-700 font-semibold">{formatINR(r.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Section>

                    <Section title="Coupon performance" empty="No coupons redeemed in this period." icon={Tag}>
                        {data.coupons.length > 0 && (
                            <div className="bg-white border border-stone-200 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="bg-stone-50 border-b border-stone-200">
                                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Code</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Orders</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Unique customers</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Discount given</th>
                                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Net revenue</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.coupons.map(c => (
                                            <tr key={c.code} className="border-b border-stone-100 hover:bg-sky-50/40">
                                                <td className="p-3 font-mono font-semibold text-stone-900">{c.code}</td>
                                                <td className="p-3 text-right">{c.orders}</td>
                                                <td className="p-3 text-right">{c.unique_customers}</td>
                                                <td className="p-3 text-right text-amber-700 font-mono">{formatINR(c.discount)}</td>
                                                <td className="p-3 text-right font-mono text-emerald-700 font-semibold">{formatINR(c.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Section>
                </>
            )}
        </div>
    );
}

function L({ label, children }) {
    return <label className="block"><span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span><div className="mt-1">{children}</div></label>;
}
function Kpi({ label, v, sub }) {
    return <div className="bg-white border border-stone-200 p-4"><div className="label-eyebrow text-stone-500">{label}</div><div className="font-serif text-2xl text-stone-900 mt-1">{v}</div>{sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}</div>;
}
function Section({ title, empty, icon: Icon, children }) {
    return (
        <div>
            <h2 className="font-serif text-xl text-stone-900 mb-2 flex items-center gap-2"><Icon className="w-4 h-4 text-sky-700" />{title}</h2>
            {React.Children.count(children) === 0 || (Array.isArray(children) && children.length === 0)
                ? <div className="bg-white border border-stone-200 p-6 text-center text-xs text-stone-500">{empty}</div>
                : children || <div className="bg-white border border-stone-200 p-6 text-center text-xs text-stone-500">{empty}</div>}
        </div>
    );
}
